// src/components/Reports/ServiceChart.jsx
import React, { useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";

const ServiceChart = ({
    title,
    chartData,
    showPatients,
    showOptions,
    investigationType,
    selectedInvestigationType,
    setSelectedInvestigationType,
    pathologyData,
    radiologyData,
    showToggle = true
}) => {
    const [selectedLabel, setSelectedLabel] = useState(null);
    const [viewMode, setViewMode] = useState("graph"); // "graph" | "patients"

    // 🔹 Decide which dataset to use
    const baseData =
        selectedInvestigationType === "PATHOLOGY"
            ? pathologyData
            : selectedInvestigationType === "RADIOLOGY"
                ? radiologyData
                : chartData;

    // 🔹 Apply label filter
    const filteredData = selectedLabel
        ? baseData.filter((item) => item.label === selectedLabel)
        : baseData;

    // Totals for summary cards
    const totalQuantity = filteredData.reduce(
        (sum, item) => sum + (item.quantity || 0),
        0
    );
    const totalRevenue = filteredData.reduce(
        (sum, item) => sum + (item.revenue || 0),
        0
    );

    return (
        <div className="bg-white rounded-xl shadow p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-md font-semibold text-[#003A71]">{title}</h3>
            </div>

            <div className="flex gap-6">
                {/* Left Summary Section */}
                <div className="w-[40%] space-y-4">
                    <h3 className="text-md font-semibold text-[#003A71]">Summary </h3>

                    {/* Label Buttons */}
                    {baseData.length > 0 && <div className="flex flex-wrap gap-3 mb-4 max-h-[12rem] overflow-y-auto w-full border rounded-lg p-3 bg-white">
                        {baseData.map((item) => (
                            <div
                                key={item.label}
                                className={`p-2 rounded-md border bg-white shadow-sm cursor-pointer 
        ${selectedLabel === item.label ? "border-blue-400" : "border-gray-200"}`}
                                onClick={() =>
                                    setSelectedLabel(selectedLabel === item.label ? null : item.label)
                                }
                            >
                                <div
                                    className={`text-sm font-medium ${selectedLabel === item.label ? "text-blue-600" : "text-gray-700"
                                        }`}
                                >
                                    {item.label}
                                </div>
                            </div>
                        ))}
                    </div>
                    }





                    {/* Summary Cards */}
                    <div className="p-2 rounded-lg border bg-white">
                        <div className="text-sm text-gray-600">Total Quantity</div>
                        <div className="text-xl font-bold text-[#ed7d31]">
                            {totalQuantity}
                        </div>
                    </div>

                    <div className="p-2 rounded-lg border bg-white">
                        <div className="text-sm text-gray-600">Total Revenue</div>
                        <div className="text-xl font-bold text-[#00b0f0]">
                            ₹{totalRevenue.toFixed(2)}
                        </div>
                    </div>
                </div>

                {/* Right Section (Chart / Patient List Toggle) */}
                <div className="w-[70%]">
                    {/* Toggle Buttons */}
                    <div className="flex items-center mb-4 gap-4">
                        <h3 className="text-sm font-semibold text-[#003A71]">Revenue Distribution</h3>

                        <div className="flex items-center gap-10 ml-auto">
                            {/* Toggle Buttons */}
                            <div className="flex gap-2">
                                <button
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium ${viewMode === "graph"
                                        ? "bg-blue-500 text-white"
                                        : "bg-gray-100 text-gray-600"
                                        }`}
                                    onClick={() => setViewMode("graph")}
                                >
                                    Graph
                                </button>
                                <button
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium ${viewMode === "patients"
                                        ? "bg-blue-500 text-white"
                                        : "bg-gray-100 text-gray-600"
                                        }`}
                                    onClick={() => setViewMode("patients")}
                                >
                                    Patient List
                                </button>
                            </div>
                            {/* Service Type Selector */}
                            {investigationType && (
                                <select
                                    className="bg-white text-gray-600 px-8 py-3 rounded-md border border-gray-300 text-sm"
                                    value={selectedInvestigationType}
                                    onChange={(e) => setSelectedInvestigationType(e.target.value)}
                                >
                                    {investigationType.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>


                    {/* Conditional Rendering */}
                    {viewMode === "graph" ? (
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart
                                data={filteredData}
                                margin={{ top: 10, right: 20, left: 0, bottom: 40 }}
                                barCategoryGap="20%"
                            >
                                <CartesianGrid vertical={false} stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="label"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 12, fill: "#666" }}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 12, fill: "#666" }}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                                    itemStyle={{ fontSize: 12 }}
                                    formatter={(val, name) =>
                                        name === "Revenue"
                                            ? [`₹${val.toFixed(2)}`, name]
                                            : [val, name]
                                    }
                                />
                                <Legend
                                    verticalAlign="bottom"
                                    height={40}
                                    wrapperStyle={{
                                        fontSize: 12,
                                        paddingTop: 12,
                                        display: "flex",
                                        justifyContent: "center",
                                        lineHeight: "20px",
                                    }}
                                />
                                <Bar
                                    dataKey="quantity"
                                    name="Quantity Sold"
                                    fill="#ed7d31"
                                    radius={[4, 4, 0, 0]}
                                />
                                <Bar
                                    dataKey="revenue"
                                    name="Revenue"
                                    fill="#00b0f0"
                                    radius={[4, 4, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="p-3 rounded-lg border bg-white max-h-100 overflow-y-auto">
                            <h4 className="text-md font-semibold text-[#003A71] mb-2">
                                Patients
                            </h4>
                            {selectedLabel ? (
                                <div className="mb-4">
                                    <div className="font-medium text-gray-700">{selectedLabel}</div>
                                    {filteredData.length > 0 && filteredData[0]?.patients?.length > 0 ? (
                                        <ul className="list-disc list-inside text-sm text-gray-600 pl-4">
                                            {filteredData[0].patients.map((p, i) => (
                                                <li className="my-2" key={i}>
                                                    {p}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-gray-500 italic">No patients found</p>
                                    )}

                                </div>
                            ) : (
                                <div className="text-sm text-gray-500 italic">
                                    Please select a drug to see patient details.
                                </div>
                            )}

                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ServiceChart;
